import React, { useState, useEffect } from 'react';
import { Bell, Settings, Trash2, Check, AlertCircle, TrendingUp, Target, CreditCard } from 'lucide-react';

interface Notification {
  id: string;
  type: 'campaign' | 'budget' | 'billing' | 'performance' | 'system';
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  campaign_id?: string;
  campaign_name?: string;
  metadata?: any;
}

const NotificationsView: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    loadNotifications();
  }, [filter, typeFilter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call
      // const data = await adsApiService.getNotifications({ unreadOnly: filter === 'unread', type: typeFilter });
      
      // For now, return empty array (no demo data)
      setNotifications([]);
    } catch (err) {
      console.error('Error loading notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      // TODO: API call to mark as read
      // await adsApiService.markNotificationAsRead(notificationId);
      
      setNotifications(prev => prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, is_read: true }
          : notification
      ));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      // TODO: API call to mark all as read
      // await adsApiService.markAllNotificationsAsRead();
      
      setNotifications(prev => prev.map(notification => ({ ...notification, is_read: true })));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      // TODO: API call to delete notification
      // await adsApiService.deleteNotification(notificationId);
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'campaign':
        return <Target className="text-blue-500" size={20} />;
      case 'budget':
        return <AlertCircle className="text-orange-500" size={20} />;
      case 'billing':
        return <CreditCard className="text-green-500" size={20} />;
      case 'performance':
        return <TrendingUp className="text-purple-500" size={20} />;
      case 'system':
        return <Bell className="text-gray-500" size={20} />;
      default:
        return <Bell className="text-gray-500" size={20} />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return date.toLocaleDateString('es-ES');
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <div className="p-4 lg:p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-gray-200 h-20 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 overflow-x-hidden min-w-0">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 lg:mb-8 gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
            <Bell className="text-blue-600" size={24} />
            Notificaciones
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-sm font-medium px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          <p className="text-gray-600 text-sm lg:text-base">Mantente al día con tus campañas publicitarias</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setFilter(filter === 'all' ? 'unread' : 'all')}
            className={`px-3 lg:px-4 py-2 rounded-lg transition-colors text-sm lg:text-base whitespace-nowrap ${
              filter === 'unread'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter === 'all' ? 'Solo no leídas' : 'Todas'}
          </button>
          
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm lg:text-base whitespace-nowrap"
            >
              <Check size={16} />
              <span className="hidden lg:inline">Marcar todas como leídas</span>
              <span className="lg:hidden">Leer todas</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Settings size={16} className="text-gray-500" />
          <span className="font-medium text-gray-700 text-sm lg:text-base">Filtros</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'Todas', count: notifications.length },
            { value: 'campaign', label: 'Campañas', count: notifications.filter(n => n.type === 'campaign').length },
            { value: 'budget', label: 'Presupuesto', count: notifications.filter(n => n.type === 'budget').length },
            { value: 'billing', label: 'Facturación', count: notifications.filter(n => n.type === 'billing').length },
            { value: 'performance', label: 'Rendimiento', count: notifications.filter(n => n.type === 'performance').length },
            { value: 'system', label: 'Sistema', count: notifications.filter(n => n.type === 'system').length },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => setTypeFilter(option.value)}
              className={`px-3 py-1.5 text-xs lg:text-sm rounded-full transition-colors whitespace-nowrap ${
                typeFilter === option.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label} ({option.count})
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`bg-white border rounded-lg p-4 lg:p-6 hover:shadow-md transition-all ${
                !notification.is_read 
                  ? 'border-blue-200 bg-blue-50/30' 
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {getNotificationIcon(notification.type)}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-2 lg:gap-4 mb-2">
                    <h3 className={`font-semibold text-sm lg:text-base ${
                      !notification.is_read ? 'text-gray-900' : 'text-gray-700'
                    }`}>
                      {notification.title}
                    </h3>
                    
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs lg:text-sm text-gray-500">
                        {formatTimeAgo(notification.created_at)}
                      </span>
                      
                      {!notification.is_read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                          title="Marcar como leída"
                        >
                          <Check size={14} />
                        </button>
                      )}
                      
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Eliminar notificación"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  
                  <p className="text-gray-700 text-sm lg:text-base mb-2">{notification.message}</p>
                  
                  {notification.campaign_name && (
                    <div className="flex items-center gap-2 mt-3">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                        {notification.campaign_name}
                      </span>
                    </div>
                  )}
                  
                  {!notification.is_read && (
                    <div className="absolute top-4 right-4 w-2 h-2 bg-blue-500 rounded-full"></div>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <Bell size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'unread' ? 'No hay notificaciones sin leer' : 'No hay notificaciones'}
            </h3>
            <p className="text-gray-500 text-sm lg:text-base">
              {filter === 'unread' 
                ? 'Todas tus notificaciones están al día'
                : 'Las notificaciones sobre tus campañas aparecerán aquí'
              }
            </p>
          </div>
        )}
      </div>

      {/* Setup Notice */}
      <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 lg:p-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-blue-600 rounded-xl">
            <Bell className="text-white" size={24} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 mb-2">🔔 Configurar Notificaciones</h3>
            <p className="text-gray-700 text-sm lg:text-base mb-4">
              Recibe alertas sobre el rendimiento de tus campañas, límites de presupuesto, facturas y más.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4 text-xs lg:text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span><strong>Campañas:</strong> Estados y resultados</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span><strong>Presupuesto:</strong> Límites y gastos</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span><strong>Facturación:</strong> Pagos y facturas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <span><strong>Rendimiento:</strong> Métricas importantes</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsView;